// SiteDetail — /dashboard/sites/:siteId
//
// Single-site landing page that complements the list (/dashboard/sites)
// and the floor-stack visual (/dashboard/sites/:siteId/floor-stack).
// Displays site metadata + every building's floors + per-floor "edit
// layout" deep links + create-building / create-floor drawers.
//
// Backed by:
//   GET  /v1/sites/:id            (extended in this PR to JOIN client)
//   GET  /v1/sites/:id/floor-stack (composite read for buildings/floors)
//   POST /v1/buildings
//   POST /v1/floors
//
// Hook order: every hook is at the top of the component before any
// early-return (same invariant FloorStackContainer relies on, so we
// don't reintroduce React #310).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2,
  Layers,
  MapPin,
  Plus,
  RefreshCw,
  ServerCrash,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import Drawer from "../components/Drawer.js";
import { Field } from "../components/Field.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { getSite, friendlySitesError, type SiteRow } from "../lib/sitesApi.js";
import {
  getFloorStack,
  createBuilding,
  createFloor,
  friendlyFloorStackError,
  type FloorStackResponse,
} from "../lib/floorStackApi.js";

type DetailState =
  | { status: "loading" }
  | { status: "ready"; site: SiteRow; stack: FloorStackResponse }
  | { status: "error"; message: string };

type StackBuilding = FloorStackResponse["buildings"][number];

type DrawerKind =
  | { kind: "new_building" }
  | { kind: "new_floor"; building: StackBuilding };

const RISK_BADGE: Record<SiteRow["risk_level"], string> = {
  low: "badge-default",
  medium: "badge-warning",
  high: "badge-danger",
};
const RISK_LABEL: Record<SiteRow["risk_level"], string> = {
  low: "低",
  medium: "中",
  high: "高",
};
const STATUS_BADGE: Record<SiteRow["status"], string> = {
  active: "badge-success",
  inactive: "badge-default",
};
const STATUS_LABEL: Record<SiteRow["status"], string> = {
  active: "啟用",
  inactive: "已封存",
};

export default function SiteDetail(): React.ReactElement {
  const { siteId = "" } = useParams<{ siteId: string }>();
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const canWrite =
    session.scopes.includes("write:site") ||
    session.scopes.includes("admin:tenant") ||
    session.super_admin === true;

  const [state, setState] = useState<DetailState>({ status: "loading" });
  const [drawer, setDrawer] = useState<DrawerKind | null>(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    setState({ status: "loading" });
    try {
      const [site, stack] = await Promise.all([
        getSite(siteId, tenantParam),
        getFloorStack(siteId, tenantParam),
      ]);
      setState({ status: "ready", site, stack });
    } catch (err) {
      const msg = err && typeof err === "object" && "status" in err ? friendlySitesError(err) : friendlyFloorStackError(err);
      setState({ status: "error", message: msg });
    }
  }, [siteId, tenantParam]);

  useEffect(() => {
    void load();
  }, [load]);

  const buildings = useMemo<FloorStackResponse["buildings"]>(
    () => (state.status === "ready" ? state.stack.buildings : []),
    [state],
  );

  // ── early returns AFTER all hooks ─────────────────────────────────────
  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="案場詳情" description="顯示單一案場的基本資料、建築物與樓層配置。" />
        <TenantPickerEmptyState />
      </>
    );
  }

  if (state.status === "loading") {
    return (
      <>
        <PageHeader title="案場詳情" description="載入中…" />
        <DetailSkeleton />
      </>
    );
  }
  if (state.status === "error") {
    return (
      <>
        <PageHeader title="案場詳情" description="—" />
        <EmptyState
          icon={ServerCrash}
          title="案場資料載入失敗"
          description={state.message}
          action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>}
        />
      </>
    );
  }

  const { site, stack } = state;

  return (
    <>
      <PageHeader
        title={site.name}
        description={[site.address ?? "—", site.client_name ?? "（未指派客戶）"].join(" · ")}
        actions={
          <>
            <button className="btn-secondary text-sm" onClick={() => void load()}>
              <RefreshCw className="h-4 w-4" /> 重新整理
            </button>
            <Link to={`/dashboard/sites/${site.id}/floor-stack`} className="btn-secondary text-sm">
              <Layers className="h-4 w-4" /> 打開樓層預覽
            </Link>
            <Link to="/dashboard/sites" className="btn text-sm">回案場列表</Link>
          </>
        }
      />

      <section className="card mb-4 p-4">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">案場資料</h2>
        <dl className="grid grid-cols-2 gap-y-2 text-sm md:grid-cols-4">
          <Cell label="客戶">{site.client_name ?? "—"}</Cell>
          <Cell label="地址">
            <span className="inline-flex items-start gap-1.5">
              <MapPin className="mt-0.5 h-3.5 w-3.5 text-text-muted" />
              <span className="text-text-secondary">{site.address ?? "—"}</span>
            </span>
          </Cell>
          <Cell label="風險等級">
            <span className={RISK_BADGE[site.risk_level]}>{RISK_LABEL[site.risk_level]}</span>
          </Cell>
          <Cell label="狀態">
            <span className={STATUS_BADGE[site.status]}>{STATUS_LABEL[site.status]}</span>
          </Cell>
          <Cell label="地點">
            <span className="font-mono tabular-nums text-text-secondary">{site.location_count}</span>
          </Cell>
          <Cell label="在勤人員">
            <span className="font-mono tabular-nums text-text-secondary">{site.active_officers}</span>
          </Cell>
          <Cell label="時區">
            <span className="font-mono text-text-secondary">{site.timezone}</span>
          </Cell>
          <Cell label="主色">
            {stack.site.brand_primary ? (
              <span className="inline-flex items-center gap-1.5 text-text-secondary">
                <span
                  aria-hidden
                  className="h-3 w-3 rounded-full border border-border"
                  style={{ background: stack.site.brand_primary }}
                />
                <span className="font-mono">{stack.site.brand_primary}</span>
              </span>
            ) : (
              <span className="text-text-muted">未設定</span>
            )}
          </Cell>
        </dl>
      </section>

      <section className="card mb-4">
        <header className="flex items-center justify-between border-b border-border p-4">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">建築物 / 樓層</h2>
            <p className="mt-0.5 text-xs text-text-muted">
              共 {buildings.length} 棟 · {buildings.reduce((s, b) => s + b.floors.length, 0)} 樓層
            </p>
          </div>
          {canWrite && (
            <button className="btn-primary text-sm" onClick={() => setDrawer({ kind: "new_building" })}>
              <Plus className="h-4 w-4" /> 新增建築物
            </button>
          )}
        </header>

        {buildings.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={Building2}
              title="這個案場尚未建立任何建築物"
              description="先新增一棟建築物，然後在每棟下加入樓層，才能上傳圖資並進入樓層中控。"
              action={canWrite ? (
                <button className="btn-primary text-sm" onClick={() => setDrawer({ kind: "new_building" })}>
                  <Plus className="h-4 w-4" /> 新增建築物
                </button>
              ) : undefined}
            />
          </div>
        ) : (
          <div className="divide-y divide-border">
            {buildings.map((b) => (
              <BuildingBlock
                key={b.id}
                site={site}
                building={b}
                canWrite={canWrite}
                onAddFloor={() => setDrawer({ kind: "new_floor", building: b })}
              />
            ))}
          </div>
        )}
      </section>

      <Drawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        title={drawer?.kind === "new_floor" ? `為「${drawer.building.name}」新增樓層` : "新增建築物"}
        description={drawer?.kind === "new_floor"
          ? "樓層編號、顯示名稱、是否為重點樓層"
          : "建築物名稱與顯示順序，建立後再為它加入樓層"}
        size="md"
      >
        {drawer?.kind === "new_building" && (
          <NewBuildingForm
            siteId={site.id}
            tenantId={tenantParam}
            onClose={() => setDrawer(null)}
            onSaved={() => {
              setDrawer(null);
              void load();
            }}
          />
        )}
        {drawer?.kind === "new_floor" && (
          <NewFloorForm
            building={drawer.building}
            existingFloors={drawer.building.floors}
            tenantId={tenantParam}
            onClose={() => setDrawer(null)}
            onSaved={() => {
              setDrawer(null);
              void load();
            }}
          />
        )}
      </Drawer>
    </>
  );
}

interface BuildingBlockProps {
  site: SiteRow;
  building: StackBuilding;
  canWrite: boolean;
  onAddFloor: () => void;
}

function BuildingBlock({ site, building, canWrite, onAddFloor }: BuildingBlockProps): React.ReactElement {
  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">{building.name}</h3>
          <p className="text-xs text-text-muted">
            {building.floors.length} 樓層 · 顯示順序 {building.display_order}
          </p>
        </div>
        {canWrite && (
          <button className="btn-secondary text-xs" onClick={onAddFloor}>
            <Plus className="h-3.5 w-3.5" /> 新增樓層
          </button>
        )}
      </div>

      {building.floors.length === 0 ? (
        <p className="rounded border border-dashed border-border p-3 text-xs text-text-muted">
          尚未加入任何樓層。
        </p>
      ) : (
        <ul className="divide-y divide-border rounded border border-border">
          {[...building.floors]
            .sort((a, b) => a.display_order - b.display_order)
            .map((f) => (
              <FloorRow
                key={f.id}
                site={site}
                building={building}
                floor={f}
              />
            ))}
        </ul>
      )}
    </div>
  );
}

interface FloorRowProps {
  site: SiteRow;
  building: StackBuilding;
  floor: StackBuilding["floors"][number];
}

function FloorRow({ site, building, floor }: FloorRowProps): React.ReactElement {
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-text-primary">{floor.display_label ?? floor.floor_name}</span>
          {floor.is_critical === 1 && (
            <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-300">重點樓層</span>
          )}
          {floor.status !== "active" && (
            <span className="badge-default text-[10px]">{floor.status === "disabled" ? "停用" : "已封存"}</span>
          )}
        </div>
        <p className="text-xs text-text-muted truncate">
          {floor.layout_id ? "已設定圖資" : "尚未上傳圖資"} · 顯示順序 {floor.display_order}
        </p>
      </div>
      <Link
        to={`/dashboard/sites/${site.id}/buildings/${building.id}/floors/${floor.id}/layout`}
        className="btn-secondary text-xs"
      >
        編輯圖資
      </Link>
    </li>
  );
}

function Cell({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function DetailSkeleton(): React.ReactElement {
  return (
    <>
      <div className="card mb-4 p-4">
        <Skeleton className="mb-3 h-4 w-32" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
      <div className="card p-4">
        <Skeleton className="mb-3 h-4 w-40" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    </>
  );
}

interface NewBuildingFormProps {
  siteId: string;
  tenantId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function NewBuildingForm({ siteId, tenantId, onClose, onSaved }: NewBuildingFormProps): React.ReactElement {
  const [name, setName] = useState("");
  // String state so the user can backspace down to empty without React
  // re-stamping a "0". Parsed on save with `parseIntOr` (definition at
  // file bottom).
  const [displayOrder, setDisplayOrder] = useState("0");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await createBuilding(
        {
          site_id: siteId,
          name: name.trim(),
          display_order: parseIntOr(displayOrder, 0),
          set_as_default: setAsDefault,
        },
        tenantId,
      );
      onSaved();
    } catch (e) {
      setErr(friendlyFloorStackError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Field label="建築物名稱" required>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="如：A 棟、東棟、總部大樓" />
      </Field>
      <Field label="顯示順序" hint="數字小者排前面">
        <input
          className="input font-mono"
          type="number"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
        />
      </Field>
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
        />
        設為預設建築物（樓層預覽預設展開這棟）
      </label>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
        <button
          onClick={() => void save()}
          className="btn-primary text-sm"
          disabled={busy || !name.trim()}
        >
          {busy ? "建立中…" : "建立建築物"}
        </button>
      </div>
    </div>
  );
}

interface NewFloorFormProps {
  building: StackBuilding;
  existingFloors: StackBuilding["floors"];
  tenantId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function NewFloorForm({ building, existingFloors, tenantId, onClose, onSaved }: NewFloorFormProps): React.ReactElement {
  // Default the new floor to one above the highest floor_no already in
  // the building (or 1 if empty). 1F is the standard ground reference.
  const defaultFloorNo = useMemo(() => {
    if (existingFloors.length === 0) return 1;
    return Math.max(...existingFloors.map((f) => f.floor_no)) + 1;
  }, [existingFloors]);

  const [floorNo, setFloorNo] = useState<string>(String(defaultFloorNo));
  const [floorName, setFloorName] = useState(`${defaultFloorNo}F`);
  const [displayLabel, setDisplayLabel] = useState("");
  const [isCritical, setIsCritical] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await createFloor(
        {
          building_id: building.id,
          floor_no: parseIntOr(floorNo, 1),
          floor_name: floorName.trim(),
          display_label: displayLabel.trim() || undefined,
          display_order: existingFloors.length,
          is_critical: isCritical,
        },
        tenantId,
      );
      onSaved();
    } catch (e) {
      setErr(friendlyFloorStackError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="樓層編號" required hint="負數代表地下層（B1 = -1）">
          <input
            className="input font-mono"
            type="number"
            value={floorNo}
            onChange={(e) => {
              setFloorNo(e.target.value);
              const n = parseIntOr(e.target.value, NaN);
              if (Number.isFinite(n)) setFloorName(n >= 0 ? `${n}F` : `B${Math.abs(n)}`);
            }}
          />
        </Field>
        <Field label="樓層名稱" required>
          <input className="input" value={floorName} onChange={(e) => setFloorName(e.target.value)} />
        </Field>
      </div>
      <Field label="顯示標籤" hint="出現在卡片上的文字，留空則用樓層名稱">
        <input className="input" value={displayLabel} onChange={(e) => setDisplayLabel(e.target.value)} placeholder="（選填）" />
      </Field>
      <label className="flex items-center gap-2 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={isCritical}
          onChange={(e) => setIsCritical(e.target.checked)}
        />
        標記為重點樓層（在樓層檢視中以警示色標示）
      </label>
      {err && <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{err}</div>}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary text-sm" disabled={busy}>取消</button>
        <button
          onClick={() => void save()}
          className="btn-primary text-sm"
          disabled={busy || !floorName.trim()}
        >
          {busy ? "建立中…" : "建立樓層"}
        </button>
      </div>
    </div>
  );
}


/** Parse the controlled-string value of a `<input type="number">` back to
 *  an integer for the API call. Empty / NaN falls back to `fallback` so
 *  the user can backspace down to nothing without breaking the request. */
function parseIntOr(s: string, fallback: number): number {
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : fallback;
}
